import {
  type Filters,
  type NodeType,
  type OasDocument3XExtended,
  isNullable,
} from '@ipfabric/utils';
import type { DeviceViewNodeEntity } from 'components/DeviceView/deviceViewTypes';
import { isValidNodeEndpoint } from 'components/DeviceView/helpers/isValidNodeEndpoint';
import { getSnColumns } from 'utils/oas/getSnColumns';

type ApiFilterAttribute = string;

type ApiFilterFactory = (node: DeviceViewNodeEntity) => Filters;
export const isApiFilterFactory = (
  attribute: NodeCustomFilters,
): attribute is ApiFilterFactory => typeof attribute === 'function';

type NodeCustomFilters = ApiFilterAttribute[] | ApiFilterFactory;

type CustomApiEndpointFilterSettings = {
  [apiEndpoint: string]: NodeCustomFilters;
};

type CustomNodeFilterSettings = {
  [NodeTypeEntry in NodeType]?: {
    [apiEndpoint: string]: NodeCustomFilters;
  };
};

export const hostsApiEndpoint = '/tables/addressing/hosts';

const getCustomHostFilterSettings = (
  deviceViewEntity: DeviceViewNodeEntity,
  type: 'parentNodeType' | 'nodeType',
): ApiFilterAttribute[] =>
  deviceViewEntity[type] === 'ap' ? ['accessPoints'] : ['edges', 'gateways'];

// custom filters by node type and api endpoint
const getCustomNodeFilterSettings = (
  deviceViewEntity: DeviceViewNodeEntity,
): CustomNodeFilterSettings => ({
  fex: {
    '/tables/platforms/fex/interfaces': ['fexSn'],
  },
  host: {
    [hostsApiEndpoint]: ['uniqId'],
  },
  phone: {
    [hostsApiEndpoint]: ['uniqId'],
  },
  hostGroup: {
    [hostsApiEndpoint]: getCustomHostFilterSettings(
      deviceViewEntity,
      'parentNodeType',
    ),
  },
  phoneGroup: {
    [hostsApiEndpoint]: getCustomHostFilterSettings(
      deviceViewEntity,
      'parentNodeType',
    ),
  },
});

// custom filters by api endpoint for all node types
const getCustomApiEndpointFilterSettings = (
  deviceViewEntity: DeviceViewNodeEntity,
): CustomApiEndpointFilterSettings => ({
  [hostsApiEndpoint]: getCustomHostFilterSettings(deviceViewEntity, 'nodeType'),
  '/tables/platforms/fex/modules': (deviceViewEntity) =>
    deviceViewEntity.nodeType === 'fex'
      ? {
          sn: ['eq', deviceViewEntity.sn],
        }
      : {
          parents: ['any', 'sn', 'eq', deviceViewEntity.sn],
        },
});

export const getNodeCustomFilters = (
  deviceViewEntity: DeviceViewNodeEntity,
  apiEndpoint: string,
  oas: OasDocument3XExtended,
): NodeCustomFilters => {
  const customNodeFilterSettings =
    getCustomNodeFilterSettings(deviceViewEntity)[deviceViewEntity.nodeType!];
  const customApiEndpointFilterSettings =
    getCustomApiEndpointFilterSettings(deviceViewEntity)[apiEndpoint];
  if (
    !isValidNodeEndpoint(
      deviceViewEntity.deviceViewElement,
      apiEndpoint,
      oas,
    ) &&
    isNullable(customNodeFilterSettings) &&
    isNullable(customApiEndpointFilterSettings)
  ) {
    return [];
  }
  const customNodeAttributes = customNodeFilterSettings?.[apiEndpoint];
  const customApiEndpointAttributes = customApiEndpointFilterSettings;
  const snColumns = getSnColumns(oas, apiEndpoint);

  return customNodeAttributes ?? customApiEndpointAttributes ?? snColumns ?? [];
};
